// src/pages/platform-admin/UniversityAdmin.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useMediaQuery,
    Avatar,
    Stack,
    FormControlLabel,
    Switch,
    InputLabel as MuiInputLabel
} from '@mui/material';
import {
    Search,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    School,
    CheckCircle,
    Cancel,
    Business,
    CalendarToday,
    Update,
    Email,
    Phone,
    Flag,
    Person,
    Badge,
    AdminPanelSettings,
    VerifiedUser,
    Schedule,
    Description,
    LocationCity,
    Edit as EditIcon,
    LockReset
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIInstitutionAdminList,
    APIInstitutionAdminProfileImage,
    APIInstitutionAdminDelete,
    APIInstitutionAdminUpdate,
    APIInstitutionAdminProfileById,
    APIInstitutionAdminUpdatePasswordByAdmin
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    recordsCount, 
    recordsPerPage, 
    getUserDetails,
    getImageUrl
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorState';
import LoadingState from '../../components/LoadingState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Title options
const titleOptions = [
    { value: 'Mr', label: 'Mr.' },
    { value: 'Mrs', label: 'Mrs.' },
    { value: 'Ms', label: 'Ms.' },
    { value: 'Dr', label: 'Dr.' },
    { value: 'Prof', label: 'Prof.' }
];

// Gender options
const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
];

// Status options
const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
];

// Helper function to get initials from user name
const getInitials = (name) => {
    if (!name) return 'UA';
    return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Helper function to get avatar color based on user name
const stringToColor = (string) => {
    if (!string) return '#1976d2';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
};

const UniversityAdmin = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Form data for edit
    const [formData, setFormData] = useState({
        full_name: '',
        title: '',
        email: '',
        contact_phone: '',
        nationality: '',
        gender: '',
        biography: '',
        status: 'active',
        is_active: true,
        is_verified: false,
        university_id: '',
        university_name: ''
    });
    
    // Password form data
    const [passwordData, setPasswordData] = useState({
        new_password: '',
        new_password_confirmation: ''
    });
    
    // Form errors
    const [formErrors, setFormErrors] = useState({});
    
    // Show password change section
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    
    const [filters, setFilters] = useState({
        status: '',
        is_active: '',
        university_id: ''
    });
    
    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    
    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };
    
    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        
        return () => {
            isMounted.current = false;
        };
    }, [showToast]);
    
    useEffect(() => {
        fetchData();
    }, []);
    
    // Fetch university admins
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.list(APIInstitutionAdminList);
            
            if (result.success && isMounted.current) {
                const admins = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: admins
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);
    
    // Filter based on search term and filters
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                (record.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (record.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (record.registration_id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (record.admin_id_number?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            const matchesStatus = !filters.status || record.status === filters.status;
            const matchesActive = filters.is_active === '' || 
                (filters.is_active === 'active' && record.is_active) ||
                (filters.is_active === 'inactive' && !record.is_active);
            
            return matchesSearch && matchesStatus && matchesActive;
        });
    }, [pageState.pageData, searchTerm, filters.status, filters.is_active]);
    
    // Pagination handlers
    const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);
    
    // Dialog handlers
    const handlefilterOptions = useCallback(() => setFilterOptions(true), []);
    const handleCloseOptions = useCallback(() => setFilterOptions(false), []);
    
    const handleOpenView = useCallback((record) => {
        setSelectedRecord(record);
        setOpenView(true);
    }, []);
    
    const handleCloseView = useCallback(() => setOpenView(false), []);
    
    const handleOpenEdit = useCallback((record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        
        setFormData({
            full_name: record.full_name || '',
            title: record.title || 'Mr',
            email: record.email || '',
            contact_phone: record.contact_phone || '',
            nationality: record.nationality || '',
            gender: record.gender || 'male',
            biography: record.biography || '',
            status: record.status || 'active',
            is_active: record.is_active !== undefined ? record.is_active : true,
            is_verified: record.is_verified || false,
            university_id: record.university_id || '',
            university_name: record.university_name || ''
        });
        
        setPasswordData({
            new_password: '',
            new_password_confirmation: ''
        });
        setShowPasswordSection(false);
        setFormErrors({});
        setOpenEdit(true);
    }, []);
    
    const handleCloseEdit = useCallback(() => {
        setOpenEdit(false);
        setShowPasswordSection(false);
        setFormErrors({});
    }, []);
    
    const handleOpenDelete = useCallback((record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    }, []);
    
    const handleCloseDelete = useCallback(() => setOpenDelete(false), []);
    
    // Filter handlers
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const clearFilters = useCallback(() => {
        setFilters({
            status: '',
            is_active: '',
            university_id: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    }, [handleCloseOptions]);

    // Get profile image URL
    const getProfileImageUrl = (record) => {
        if (record?.profile_image_path) {
            return getImageUrl(APIInstitutionAdminProfileImage, record.profile_image_path);
        }
        return null;
    };

    // Form input change handler
    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    }, [formErrors]);

    // Password input change handler
    const handlePasswordChange = useCallback((e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    }, [formErrors]);

    // Validate edit form
    const validateEditForm = useCallback(() => {
        const errors = {};
        
        if (!formData.full_name?.trim()) {
            errors.full_name = 'Full name is required';
        }
        
        if (!formData.email?.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        
        if (!formData.contact_phone?.trim()) {
            errors.contact_phone = 'Contact phone is required';
        }
        
        if (!formData.nationality?.trim()) {
            errors.nationality = 'Nationality is required';
        }
        
        if (!formData.gender) {
            errors.gender = 'Gender is required';
        }
        
        if (!formData.status) {
            errors.status = 'Status is required';
        }
        
        // Validate password fields if section is shown
        if (showPasswordSection) {
            if (!passwordData.new_password || passwordData.new_password.length < 8) {
                errors.new_password = 'Password must be at least 8 characters';
            }
            if (!passwordData.new_password_confirmation) {
                errors.new_password_confirmation = 'Please confirm your password';
            }
            if (passwordData.new_password && passwordData.new_password_confirmation && 
                passwordData.new_password !== passwordData.new_password_confirmation) {
                errors.new_password_confirmation = 'Passwords do not match';
            }
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, passwordData, showPasswordSection]);

    // Handle edit submission
    const handleEditSubmit = useCallback(async (e) => {
        e?.preventDefault?.();
        
        if (!validateEditForm()) {
            showToast("Validation Error", "Please fix all errors before submitting", "warning");
            return;
        }
        
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('updateId', updateId);
        formDataToSend.append('full_name', formData.full_name);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('contact_phone', formData.contact_phone);
        formDataToSend.append('nationality', formData.nationality);
        formDataToSend.append('gender', formData.gender);
        formDataToSend.append('biography', formData.biography || '');
        formDataToSend.append('status', formData.status);
        formDataToSend.append('is_active', formData.is_active ? '1' : '0');
        formDataToSend.append('is_verified', formData.is_verified ? '1' : '0');
        
        try {
            // First update profile
            const result = await apiService.update(APIInstitutionAdminUpdate, formDataToSend);
            
            if (result && result.success && isMounted.current) {
                // If password update is requested
                if (showPasswordSection && passwordData.new_password) {
                    const passwordFormData = new FormData();
                    passwordFormData.append('user_id', getUserDetails("Id"));
                    passwordFormData.append('updateId', updateId);
                    passwordFormData.append('new_password', passwordData.new_password);
                    passwordFormData.append('new_password_confirmation', passwordData.new_password_confirmation);
                    
                    const passwordResult = await apiService.update(APIInstitutionAdminUpdatePasswordByAdmin, passwordFormData);
                    
                    if (passwordResult && passwordResult.success) {
                        showToast("Success", "Profile and password updated successfully", "success");
                    } else {
                        showToast("Warning", "Profile updated but password change failed: " + (passwordResult?.message || ''), "warning");
                    }
                } else {
                    showToast("Success", "University administrator updated successfully", "success");
                }
                
                handleCloseEdit();
                fetchData();
            } else {
                showToast("Error", result?.message || "Update failed", "error");
            }
        } catch (error) {
            console.error("Update API Error:", error);
            showToast("Error", error.response?.data?.message || "Update failed", "error");
        }
    }, [updateId, formData, passwordData, showPasswordSection, validateEditForm, showToast, handleCloseEdit, fetchData]);

    // Handle delete submission
    const handleDeleteSubmit = useCallback(async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('updateId', updateId);

        try {
            const result = await apiService.delete(APIInstitutionAdminDelete, formDataToSend);
            
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseDelete();
                fetchData();
            } else if (result && !result.success) {
                showToast("Error", result.message || "Delete operation failed", "error");
            }
        } catch (error) {
            console.error("Delete API Error:", error);
            showToast("Error", error.response?.data?.message || "Delete operation failed", "error");
        }
    }, [updateId, showToast, handleCloseDelete, fetchData]);

    // Reset edit form
    const resetEditForm = useCallback(() => {
        setFormData({
            full_name: '',
            title: 'Mr',
            email: '',
            contact_phone: '',
            nationality: '',
            gender: 'male',
            biography: '',
            status: 'active',
            is_active: true,
            is_verified: false,
            university_id: '',
            university_name: ''
        });
        setPasswordData({
            new_password: '',
            new_password_confirmation: ''
        });
        setShowPasswordSection(false);
        setFormErrors({});
    }, []);

    if (loading) {
        return <LoadingState message="Loading University Admins..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load University Admins" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <PlatformAdminBar notificationCount={65} />
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            {/* Title */}
                            <Typography sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                University Administrators ({recordsCount(pageState.pageData.length)} Records)
                            </Typography>

                            {/* Buttons */}
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={handlefilterOptions}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    View Options
                                </Button>
                                
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        flex: { xs: 1, sm: 'none' }
                                    }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {/* Search Bar */}
                            <Paper sx={{
                                width: { xs: '100%', sm: '300px' },
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: { xs: 0, sm: 'auto' },
                                borderRadius: '8px',
                                backgroundColor: theme.palette.grey[50],
                            }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search by name, email, or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Search sx={{ mr: 1, color: theme.palette.grey[600] }} />,
                                        sx: {
                                            '& fieldset': { border: 'none' },
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                    size="small"
                                />
                            </Paper>
                        </Box>

                        {/* Active Filters */}
                        {(filters.status || filters.is_active) && (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Active filters:
                                </Typography>
                                {filters.status && (
                                    <Chip
                                        label={`Status: ${filters.status}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.is_active && (
                                    <Chip
                                        label={`Account: ${filters.is_active === 'active' ? 'Active' : 'Inactive'}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, is_active: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                <Button
                                    size="small"
                                    onClick={clearFilters}
                                    startIcon={<CloseIcon />}
                                    sx={{ ml: 1 }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
                
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 800, sm: 900, md: 1000 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Administrator</TableCell>
                                        <TableCell>Contact Info</TableCell>
                                        <TableCell>Admin ID</TableCell>
                                        <TableCell>University</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Account</TableCell>
                                        <TableCell>Registered</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => {
                                        const avatarColor = stringToColor(record.full_name);
                                        const initials = getInitials(record.full_name);
                                        const profileImage = getProfileImageUrl(record);
                                        
                                        return (
                                            <TableRow
                                                key={record.id}
                                                hover
                                                sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: theme.palette.action.hover
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Chip
                                                        label={page * rowsPerPage + index + 1}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={profileImage}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                bgcolor: !profileImage ? avatarColor : 'transparent',
                                                                fontSize: '1rem',
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {!profileImage && initials}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                                {record.full_name}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Badge sx={{ fontSize: 12 }} />
                                                                Reg ID: {record.registration_id}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.5}>
                                                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            <Email fontSize="small" color="action" />
                                                            {record.email}
                                                        </Typography>
                                                        {record.contact_phone && (
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                <Phone fontSize="small" color="action" />
                                                                {record.contact_phone}
                                                            </Typography>
                                                        )}
                                                        {record.nationality && (
                                                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                                <Flag fontSize="small" color="action" />
                                                                {record.nationality}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={<Business fontSize="small" />}
                                                        label={record.admin_id_number}
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={<School fontSize="small" />}
                                                        label={record.university_name || 'N/A'}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={record.status === 'active' ? <CheckCircle /> : <Cancel />}
                                                        label={record.status_label}
                                                        color={record.status === 'active' ? "success" : "error"}
                                                        size="small"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={record.is_active ? <CheckCircle /> : <Cancel />}
                                                        label={record.status_text}
                                                        color={record.is_active ? "success" : "error"}
                                                        size="small"
                                                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                    {formatCreatedAtDate(record.created_at)}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton onClick={() => handleOpenView(record)} color="primary" size="small">
                                                                <ViewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <IconButton onClick={() => handleOpenEdit(record)} color="secondary" size="small">
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton onClick={() => handleOpenDelete(record)} color="error" size="small">
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: { xs: 6, sm: 8 },
                                color: 'text.secondary'
                            }}>
                                <AdminPanelSettings sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No university administrators found
                                </Typography>
                                <Typography variant="body2">Try adjusting your search query or filters</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Filter Options
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show All Records
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Status
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Account Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={filters.status}
                                            onChange={handleFilterChange}
                                            label="Account Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                            <MenuItem value="suspended">Suspended</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Active Status</InputLabel>
                                        <Select
                                            name="is_active"
                                            value={filters.is_active}
                                            onChange={handleFilterChange}
                                            label="Active Status"
                                        >
                                            <MenuItem value="">All</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button
                            color="error"
                            onClick={handleCloseOptions}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleCloseOptions}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Dialog */}
                <Dialog
                    open={openView}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseView();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        <b>University Administrator Details</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                {/* Header with Avatar */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2, 
                                    mb: 3,
                                    flexDirection: { xs: 'column', sm: 'row' }
                                }}>
                                    <Avatar
                                        src={getProfileImageUrl(selectedRecord)}
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            bgcolor: !getProfileImageUrl(selectedRecord) ? stringToColor(selectedRecord.full_name) : 'transparent',
                                            fontSize: '2rem',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {!getProfileImageUrl(selectedRecord) && getInitials(selectedRecord.full_name)}
                                    </Avatar>
                                    <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                            {selectedRecord.full_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Registration ID: {selectedRecord.registration_id}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                                            <Chip
                                                icon={selectedRecord.status === 'active' ? <CheckCircle /> : <Cancel />}
                                                label={selectedRecord.status_label}
                                                color={selectedRecord.status === 'active' ? "success" : "error"}
                                                size="small"
                                            />
                                            <Chip
                                                icon={selectedRecord.is_active ? <CheckCircle /> : <Cancel />}
                                                label={selectedRecord.status_text}
                                                color={selectedRecord.is_active ? "success" : "error"}
                                                size="small"
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Personal Information
                                        </Typography>
                                        
                                        <Stack spacing={1.5}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person fontSize="small" color="action" />
                                                <strong>Full Name:</strong> {selectedRecord.full_name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Badge fontSize="small" color="action" />
                                                <strong>Title:</strong> {selectedRecord.title || 'Not provided'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Flag fontSize="small" color="action" />
                                                <strong>Nationality:</strong> {selectedRecord.nationality || 'Not provided'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Business fontSize="small" color="action" />
                                                <strong>Admin ID Number:</strong> {selectedRecord.admin_id_number}
                                            </Typography>
                                        </Stack>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Contact & Institution
                                        </Typography>
                                        
                                        <Stack spacing={1.5}>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <strong>Email:</strong> {selectedRecord.email}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Phone fontSize="small" color="action" />
                                                <strong>Phone:</strong> {selectedRecord.contact_phone || 'Not provided'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <School fontSize="small" color="action" />
                                                <strong>University:</strong> {selectedRecord.university_name || 'Not assigned'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AdminPanelSettings fontSize="small" color="action" />
                                                <strong>Role:</strong> University Administrator
                                            </Typography>
                                        </Stack>
                                    </Grid>

                                    {selectedRecord.biography && (
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" sx={{ 
                                                fontWeight: 'bold', 
                                                color: theme.palette.primary.main, 
                                                mb: 2,
                                                mt: 1,
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}>
                                                <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                Biography
                                            </Typography>
                                            <Paper sx={{ p: 2, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
                                                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                    {selectedRecord.biography}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2,
                                            mt: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            System Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarToday fontSize="small" color="action" />
                                                    <strong>Registration Date:</strong> {formatCreatedAtDate(selectedRecord.registration_date)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Update fontSize="small" color="action" />
                                                    <strong>Last Login:</strong> {selectedRecord.last_login_at ? formatCreatedAtDate(selectedRecord.last_login_at) : 'Never'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CalendarToday fontSize="small" color="action" />
                                                    <strong>Created:</strong> {formatCreatedAtDate(selectedRecord.created_at)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Update fontSize="small" color="action" />
                                                    <strong>Last Updated:</strong> {formatCreatedAtDate(selectedRecord.updated_at)}
                                                </Typography>
                                            </Grid>
                                            {selectedRecord.email_verified_at && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <VerifiedUser fontSize="small" color="action" />
                                                        <strong>Email Verified:</strong> {formatCreatedAtDate(selectedRecord.email_verified_at)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseView} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Edit Dialog */}
                <Dialog
                    open={openEdit}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseEdit();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                            maxHeight: '90vh'
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        <b>Edit University Administrator</b>
                        {selectedRecord && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                ID: {selectedRecord.registration_id}
                            </Typography>
                        )}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box sx={{ p: { xs: 0.5, sm: 1 } }}>
                            <Grid container spacing={2}>
                                {/* Personal Information Section */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        color: theme.palette.primary.main,
                                        mb: 2,
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    }}>
                                        <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Personal Information
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name *"
                                        name="full_name"
                                        value={formData.full_name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.full_name}
                                        helperText={formErrors.full_name || ''}
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Person />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"} error={!!formErrors.title}>
                                        <InputLabel>Title *</InputLabel>
                                        <Select
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            label="Title *"
                                        >
                                            {titleOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.title && (
                                            <Typography variant="caption" color="error">
                                                {formErrors.title}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email *"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={!!formErrors.email}
                                        helperText={formErrors.email || ''}
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Email />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Contact Phone *"
                                        name="contact_phone"
                                        value={formData.contact_phone}
                                        onChange={handleInputChange}
                                        error={!!formErrors.contact_phone}
                                        helperText={formErrors.contact_phone || ''}
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Phone />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nationality *"
                                        name="nationality"
                                        value={formData.nationality}
                                        onChange={handleInputChange}
                                        error={!!formErrors.nationality}
                                        helperText={formErrors.nationality || ''}
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Flag />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"} error={!!formErrors.gender}>
                                        <InputLabel>Gender *</InputLabel>
                                        <Select
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            label="Gender *"
                                        >
                                            {genderOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.gender && (
                                            <Typography variant="caption" color="error">
                                                {formErrors.gender}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Institution Information */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        color: theme.palette.primary.main,
                                        mb: 2,
                                        mt: 1,
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    }}>
                                        <School sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Institution Information
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="University"
                                        value={formData.university_name || 'N/A'}
                                        disabled
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Admin ID Number"
                                        value={selectedRecord?.admin_id_number || 'N/A'}
                                        disabled
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Business />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Biography"
                                        name="biography"
                                        value={formData.biography}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={3}
                                        size={isMobile ? "small" : "medium"}
                                        placeholder="Enter administrator's biography..."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Description />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                {/* Account Settings */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        color: theme.palette.primary.main,
                                        mb: 2,
                                        mt: 1,
                                        fontSize: { xs: '0.9rem', sm: '1rem' }
                                    }}>
                                        <VerifiedUser sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Account Settings
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"} error={!!formErrors.status}>
                                        <InputLabel>Account Status *</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Account Status *"
                                        >
                                            {statusOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.status && (
                                            <Typography variant="caption" color="error">
                                                {formErrors.status}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                                color="success"
                                            />
                                        }
                                        label={formData.is_active ? "Account Active" : "Account Inactive"}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_verified"
                                                checked={formData.is_verified}
                                                onChange={handleInputChange}
                                                color="success"
                                            />
                                        }
                                        label={formData.is_verified ? "Email Verified" : "Email Not Verified"}
                                    />
                                </Grid>

                                {/* Password Change Section */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            <LockReset sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Password Settings
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            size="small"
                                            startIcon={<LockReset />}
                                            onClick={() => setShowPasswordSection(!showPasswordSection)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {showPasswordSection ? 'Cancel Password Change' : 'Change Password'}
                                        </Button>
                                    </Box>
                                    
                                    {showPasswordSection && (
                                        <Box sx={{ mt: 2 }}>
                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                Enter a new password for the administrator. Password must be at least 8 characters.
                                            </Alert>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="New Password *"
                                                        name="new_password"
                                                        type="password"
                                                        value={passwordData.new_password}
                                                        onChange={handlePasswordChange}
                                                        error={!!formErrors.new_password}
                                                        helperText={formErrors.new_password || ''}
                                                        size={isMobile ? "small" : "medium"}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LockReset />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Confirm Password *"
                                                        name="new_password_confirmation"
                                                        type="password"
                                                        value={passwordData.new_password_confirmation}
                                                        onChange={handlePasswordChange}
                                                        error={!!formErrors.new_password_confirmation}
                                                        helperText={formErrors.new_password_confirmation || ''}
                                                        size={isMobile ? "small" : "medium"}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <LockReset />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            color="error"
                            onClick={handleCloseEdit}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleEditSubmit}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                            startIcon={<Update />}
                        >
                            Update Administrator
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={openDelete}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseDelete();
                        }
                    }}
                    TransitionComponent={Transition}
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Delete University Administrator
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Are you sure you want to delete this university administrator?
                        </Typography>
                        {selectedRecord && (
                            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {selectedRecord.full_name}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Email: {selectedRecord.email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Admin ID: {selectedRecord.admin_id_number}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        icon={<School />}
                                        label={selectedRecord.university_name || 'No University'}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Chip
                                        icon={selectedRecord.is_active ? <CheckCircle /> : <Cancel />}
                                        label={selectedRecord.status_text}
                                        color={selectedRecord.is_active ? "success" : "error"}
                                        size="small"
                                    />
                                </Box>
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button
                            color="error"
                            onClick={handleCloseDelete}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteSubmit}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Confirm Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default UniversityAdmin;